<template>
  <section class="custom-reviews-wrapper">
    <div class="row">
      <div class="col-12">
        <h4>Reviews</h4>
        <p class="text-muted">Customer reviews for your calibration services</p>
      </div>
    </div>

    <div v-if="loading" class="row">
      <div class="col-12 text-center">
        <i class="fas fa-spinner fa-spin"></i> Loading reviews...
      </div>
    </div>

    <div v-else-if="reviews && reviews.length > 0" class="row">
      <div class="col-12">
        <div class="custom-reviews-summary mb-4">
          <h5>Overall Rating</h5>
          <div class="custom-rating-display">
            <div class="custom-stars">
              <i v-for="star in 5" :key="star" class="fas fa-star" :class="star <= averageRating ? 'text-warning' : 'text-muted'"></i>
            </div>
            <span class="ms-2">{{ averageRating.toFixed(1) }} out of 5 ({{ totalCount }} {{ totalCount === 1 ? 'review' : 'reviews' }})</span>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="row mb-3">
          <div class="col-md-2">
            <label class="form-label">Results per page</label>
            <select v-model="pageSize" class="form-select form-select-sm" @change="onPageSizeChange">
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="100">100</option>
            </select>
          </div>
          <div class="col-md-8 d-flex align-items-end justify-content-center">
            <z-paginate v-model="currentPage" :page-count="pageCount" @change="onPageChange" />
          </div>
          <div class="col-md-2 d-flex align-items-end justify-content-end">
            <span class="text-muted">{{ totalCount }} items</span>
          </div>
        </div>

        <div class="custom-review-list">
          <div v-for="review in sortedReviews" :key="review.firmReview" class="custom-review-card mb-3">
            <div class="custom-review-header">
              <div class="custom-review-rating">
                <i v-for="star in 5" :key="star" class="fas fa-star" :class="star <= review.rating ? 'text-warning' : 'text-muted'"></i>
              </div>
              <div class="custom-review-meta">
                <span class="custom-reviewer-name">{{ review.reviewedBy }}</span>
                <span class="custom-review-date">{{ formatDate(review.reviewedOn) }}</span>
              </div>
            </div>
            <div class="custom-review-body">
              <div class="custom-review-source">
                <span class="badge bg-secondary">{{ review.source }}</span>
              </div>
              <div class="custom-review-inspection">
                Inspection #{{ review.adasInspection }}
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination Controls (bottom) -->
        <div class="row mt-3">
          <div class="col-12 d-flex justify-content-center">
            <z-paginate v-model="currentPage" :page-count="pageCount" @change="onPageChange" />
          </div>
        </div>
      </div>
    </div>

    <div v-else class="row">
      <div class="col-12 text-center text-muted py-5">
        <i class="fas fa-star fa-3x mb-3 opacity-25"></i>
        <p>No reviews yet</p>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { locationStore } from '@/stores/location'
import moment from 'moment'
import ZPaginate from '@/components/utilities/elements/Paginate.vue'

export default {
  name: 'Reviews',
  components: {
    ZPaginate
  },
  props: {
    firm: Object
  },
  data() {
    return {
      loading: false,
      reviews: [],
      totalCount: 0,
      currentPage: 1,
      pageSize: 10
    }
  },
  computed: {
    ...mapState(locationStore, ['location']),
    launchpadFirmId() {
      return this.location?.firmId || this.firm?.id
    },
    averageRating() {
      if (!this.reviews || this.reviews.length === 0) return 0
      const sum = this.reviews.reduce((acc, review) => acc + review.rating, 0)
      return sum / this.reviews.length
    },
    sortedReviews() {
      if (!this.reviews) return []
      return [...this.reviews].sort((a, b) => {
        return new Date(b.reviewedOn) - new Date(a.reviewedOn)
      })
    },
    pageCount() {
      return Math.ceil(this.totalCount / this.pageSize)
    }
  },
  mounted() {
    this.loadReviews()
  },
  methods: {
    ...mapActions(locationStore, ['getFirmReviews']),
    async loadReviews() {
      if (!this.launchpadFirmId) return

      this.loading = true
      try {
        const offset = (this.currentPage - 1) * this.pageSize
        const response = await this.getFirmReviews(this.launchpadFirmId, offset, this.pageSize)

        console.log('Load reviews - Response:', response)
        console.log('Response type:', typeof response)

        // Handle paginated response
        if (response && typeof response === 'object' && 'items' in response) {
          this.reviews = Array.isArray(response.items) ? response.items : []
          this.totalCount = response.totalCount || 0
          console.log('Paginated - Reviews array:', this.reviews)
        } else if (Array.isArray(response)) {
          // Fallback for non-paginated response (direct array)
          this.reviews = response
          this.totalCount = this.reviews.length
          console.log('Non-paginated array - Reviews:', this.reviews)
        } else {
          // Unknown format, default to empty
          console.warn('Unexpected response format:', response)
          this.reviews = []
          this.totalCount = 0
        }

        console.log('Final reviews array:', this.reviews)
        console.log('Is array?', Array.isArray(this.reviews))
        console.log('Total count:', this.totalCount)
      } catch (error) {
        console.error('Error loading reviews:', error)
        this.reviews = []
        this.totalCount = 0
      } finally {
        this.loading = false
      }
    },
    formatDate(date) {
      return moment(date).format('MMMM D, YYYY')
    },
    onPageChange(page) {
      this.currentPage = page
      this.loadReviews()
    },
    onPageSizeChange() {
      this.currentPage = 1 // Reset to first page when changing page size
      this.loadReviews()
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-reviews-wrapper {
  padding: 20px;
}

.custom-reviews-summary {
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.custom-rating-display {
  display: flex;
  align-items: center;
  font-size: 1.2em;
}

.custom-stars i {
  font-size: 1.5em;
  margin-right: 3px;
}

.custom-review-list {
  display: flex;
  flex-direction: column;
}

.custom-review-card {
  padding: 20px;
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.custom-review-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e9ecef;
}

.custom-review-rating i {
  font-size: 1.2em;
  margin-right: 2px;
}

.custom-review-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
}

.custom-reviewer-name {
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.custom-review-date {
  font-size: 0.9em;
  color: #6c757d;
}

.custom-review-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.custom-review-source {
  display: flex;
  align-items: center;
}

.custom-review-inspection {
  color: #6c757d;
  font-size: 0.9em;
}
</style>
